import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useHeader } from '../context/HeaderContext';
import { usePostHog } from 'posthog-js/react';
import { useToast } from '../context/ToastContext';
import {
    CheckCircle,
    XCircle,
    Clock,
    Download,
    Filter,
    Copy,
    Bug,
    BarChart3,
    Smartphone,
    Calendar,
    Zap,
    AlertCircle,
    Sparkles,
    RefreshCw,
    Video,
    FileText,
    X,
    MoreVertical,
    ChevronDown,
    ListPlus
} from 'lucide-react';

import api, { executionPlanApi } from '../services/api';
import Dialog from '../components/Dialog';
import './TestResults.css';

function TestResults() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const { setHeaderLeft } = useHeader();
    const posthog = usePostHog();
    const { showToast } = useToast();

    const [execution, setExecution] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedStepIndex, setSelectedStepIndex] = useState(null);
    const [activeLogTab, setActiveLogTab] = useState('video');
    const [similarFailures, setSimilarFailures] = useState([]);
    const [logcatContent, setLogcatContent] = useState(null);
    const [logcatLoading, setLogcatLoading] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const [videoLoading, setVideoLoading] = useState(false);
    const [rerunLoading, setRerunLoading] = useState(false);
    const [zoomedImage, setZoomedImage] = useState(null);
    const [showActionsDropdown, setShowActionsDropdown] = useState(false);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [plans, setPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [isAddingToPlan, setIsAddingToPlan] = useState(false);

    // Dialog state
    const [showDialog, setShowDialog] = useState(false);
    const [dialogConfig, setDialogConfig] = useState({
        title: '',
        message: '',
        type: 'alert',
        variant: 'default'
    });

    const [showExportModal, setShowExportModal] = useState(false);
    const [exportScript, setExportScript] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    // Load execution data
    const loadExecution = useCallback(async () => {
        try {
            const response = await api.get(`/tests/executions/${id}`, {
                params: { workspaceId: currentWorkspace?.id }
            });
            const executionData = response.data;
            setExecution(executionData);

            // Find first failed step to show by default
            if (executionData.executedSteps) {
                const steps = typeof executionData.executedSteps === 'string'
                    ? JSON.parse(executionData.executedSteps)
                    : executionData.executedSteps;

                const firstFailedIndex = steps.findIndex(step => !step.success);
                setSelectedStepIndex(firstFailedIndex >= 0 ? firstFailedIndex : 0);
            }

            // TODO: Load similar failures from API
            // For now, using mock data
            setSimilarFailures([
                {
                    id: '8410',
                    name: 'Guest Checkout',
                    environment: 'Staging',
                    timeAgo: '2h ago'
                },
                {
                    id: '8392',
                    name: 'Cart Update',
                    environment: 'Production',
                    timeAgo: '5h ago'
                }
            ]);
            if (executionData.metadata?.artifacts?.video?.url) {
                setVideoUrl(executionData.metadata.artifacts?.video?.url)
            }
        } catch (error) {
            console.error('Failed to load execution:', error);
        } finally {
            setLoading(false);
        }
    }, [id, currentWorkspace?.id]);

    // Fetch logcat content when device tab is activated
    const loadLogcat = useCallback(async () => {
        if (!execution?.metadata?.sessionArn || logcatContent !== null) {
            return; // Already loaded or not available
        }

        setLogcatLoading(true);
        try {
            // Use api service which handles authentication
            const response = await api.get(`/tests/executions/${id}/artifacts/logcat`, {
                responseType: 'text'
            });
            setLogcatContent(response.data);
        } catch (error) {
            console.error('Failed to load logcat:', error);
            if (error.response?.status === 404) {
                setLogcatContent('Logcat not available for this execution.');
            } else {
                setLogcatContent('Failed to load logcat.');
            }
        } finally {
            setLogcatLoading(false);
        }
    }, [id, execution?.metadata?.sessionArn, logcatContent]);

    useEffect(() => {
        loadExecution();
    }, [loadExecution]);

    // Load logcat when device tab is activated
    useEffect(() => {
        if (activeLogTab === 'device' && execution?.metadata?.sessionArn) {
            loadLogcat();
        }
    }, [activeLogTab, execution?.metadata?.sessionArn, loadLogcat]);

    // Fetch video URL when video tab is activated
    const loadVideo = useCallback(async () => {
        if (!execution?.metadata?.sessionArn || videoUrl !== null) {
            return; // Already loaded or not available
        }

        setVideoLoading(true);
        try {
            // Fetch artifacts to get video URL
            const response = await api.get(`/tests/executions/${id}/artifacts`);
            if (response.data.available && response.data.video?.url) {
                setVideoUrl(response.data.video.url);
            } else {
                setVideoUrl(false); // Mark as unavailable
            }
        } catch (error) {
            console.error('Failed to load video:', error);
            setVideoUrl(false);
        } finally {
            setVideoLoading(false);
        }
    }, [id, execution?.metadata?.sessionArn, videoUrl]);

    useEffect(() => {
        if (activeLogTab === 'video' && execution?.metadata?.sessionArn) {
            loadVideo();
        }
    }, [activeLogTab, execution?.metadata?.sessionArn, loadVideo]);

    // Redirect to live execution if test is still running
    useEffect(() => {
        if (execution && execution.status === 'running') {
            navigate(`/test/execution/${id}`, { replace: true });
        }
    }, [execution, id, navigate]);

    const getStatusIcon = (success) => {
        return success
            ? <CheckCircle size={16} className="status-icon success" />
            : <XCircle size={16} className="status-icon error" />;
    };

    const getStatusBadge = (status) => {
        const badges = {
            success: { className: 'badge-success', label: 'Passed' },
            failed: { className: 'badge-error', label: 'Failed' },
            error: { className: 'badge-error', label: 'Error' },
            cancelled: { className: 'badge-warning', label: 'Cancelled' },
            inconclusive: { className: 'badge-purple', label: 'Inconclusive' },
            queued: { className: 'badge-outline', label: 'Queued' },
            running: { className: 'badge-outline', label: 'Running' },

        };
        return badges[status] || badges.failed;
    };

    const formatDuration = (ms) => {
        if (!ms) return '-';
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return minutes > 0
            ? `${minutes}m ${remainingSeconds}s`
            : `${seconds}s`;
    };

    const calculateMetrics = useCallback(() => {
        if (!execution?.executedSteps) return { passCount: 0, failCount: 0, totalSteps: 0, passRate: 0 };

        const steps = typeof execution.executedSteps === 'string'
            ? JSON.parse(execution.executedSteps)
            : execution.executedSteps;

        const passCount = steps.filter(s => s.success).length;
        const totalSteps = steps.length;
        const failCount = totalSteps - passCount;
        const passRate = totalSteps > 0 ? ((passCount / totalSteps) * 100).toFixed(1) : 0;

        return { passCount, failCount, totalSteps, passRate };
    }, [execution?.executedSteps]);

    const handleRerun = async (execution, e) => {
        e.stopPropagation();

        if (!execution.testGoalId) {
            setDialogConfig({
                title: 'Cannot Rerun Test',
                message: 'Cannot rerun this execution: No associated test goal found.',
                type: 'alert',
                variant: 'danger'
            });
            setShowDialog(true);
            return;
        }

        setRerunLoading(true);
        if (posthog) {
            posthog.capture('rerun_test_clicked', {
                executionId: execution.id,
                testGoalId: execution.testGoalId
            });
        }
        try {
            const response = await api.post(`/tests/goals/${execution.testGoalId}/rerun`);
            navigate(`/test/execution/${response.data.execution.id}`);
        } catch (error) {
            console.error('Error rerunning execution:', error);
            const errorMessage = error.response?.data?.error || 'Failed to rerun execution. Please try again.'
            let displayMessage = errorMessage;
            if (errorMessage.toLowerCase().includes('quota exceeded')) {
                displayMessage = (
                    <span>
                        {errorMessage}.{' '}
                        <Link to="/settings/billing" style={{ textDecoration: 'underline' }}>
                            Upgrade
                        </Link>
                    </span>
                );
            }
            setDialogConfig({
                title: 'Rerun Failed',
                message: displayMessage,
                type: 'alert',
                variant: 'danger'
            });
            setShowDialog(true);
        } finally {
            setRerunLoading(false);
        }
    };

    const handleExportReport = async () => {
        try {
            if (posthog) {
                posthog.capture('export_report_clicked', {
                    executionId: execution?.id
                });
            }

            const metrics = calculateMetrics();
            const testName = execution.goalText || `Test Run #${id?.slice(-4)}`;
            const deviceInfo = execution.metadata?.deviceName
                ? `${execution.metadata.deviceName}${execution.metadata.deviceOs ? ` (${execution.metadata.deviceOs})` : ''}`
                : 'Device information not available';

            // Generate HTML report
            const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Test Report - ${testName}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: #1f2937; background: #f9fafb; padding: 40px 20px; }
        .container { max-width: 1200px; margin: 0 auto; background: white; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); overflow: hidden; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px; }
        .header h1 { font-size: 28px; margin-bottom: 16px; }
        .header-meta { display: flex; gap: 32px; flex-wrap: wrap; font-size: 14px; opacity: 0.95; }
        .header-meta-item { display: flex; align-items: center; gap: 8px; }
        .summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; padding: 32px; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
        .summary-card { background: white; padding: 20px; border-radius: 8px; border: 1px solid #e5e7eb; }
        .summary-label { font-size: 12px; text-transform: uppercase; color: #6b7280; font-weight: 600; letter-spacing: 0.05em; }
        .summary-value { font-size: 32px; font-weight: 700; margin-top: 8px; }
        .summary-value.success { color: #10b981; }
        .summary-value.error { color: #ef4444; }
        .content { padding: 32px; }
        .section { margin-bottom: 40px; }
        .section-title { font-size: 20px; font-weight: 600; margin-bottom: 20px; color: #111827; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; }
        .step { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin-bottom: 16px; border-left: 4px solid #d1d5db; }
        .step.passed { border-left-color: #10b981; }
        .step.failed { border-left-color: #ef4444; background: #fef2f2; }
        .step-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .step-title { font-size: 16px; font-weight: 600; color: #111827; }
        .step-status { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; }
        .step-status.passed { background: #d1fae5; color: #065f46; }
        .step-status.failed { background: #fee2e2; color: #991b1b; }
        .step-details { display: grid; gap: 12px; margin-top: 12px; }
        .step-detail { font-size: 13px; }
        .step-detail strong { color: #6b7280; font-weight: 600; display: block; margin-bottom: 4px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; }
        .step-detail code { background: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-family: 'Monaco', monospace; font-size: 12px; display: inline-block; }
        .step-detail.reasoning { background: rgba(139, 92, 246, 0.1); padding: 12px; border-radius: 6px; border-left: 3px solid #8b5cf6; }
        .step-detail.error { background: #fee2e2; padding: 12px; border-radius: 6px; border-left: 3px solid #ef4444; color: #991b1b; }
        .screenshot { max-width: 100%; height: auto; border-radius: 8px; margin-top: 12px; border: 1px solid #e5e7eb; }
        .ai-analysis { background: linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%); border: 1px solid #c4b5fd; border-radius: 8px; padding: 24px; }
        .ai-analysis h3 { color: #7c3aed; margin-bottom: 12px; font-size: 16px; }
        .ai-analysis p { color: #4b5563; line-height: 1.8; }
        .footer { background: #f9fafb; padding: 24px; text-align: center; color: #6b7280; font-size: 13px; border-top: 1px solid #e5e7eb; }
        @media print { body { padding: 0; background: white; } .container { box-shadow: none; } }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>${testName}</h1>
            <div class="header-meta">
                <div class="header-meta-item">📱 ${deviceInfo}</div>
                <div class="header-meta-item">⏱️ ${formatDuration(execution.duration)}</div>
                <div class="header-meta-item">📅 ${new Date(execution.createdAt).toLocaleString()}</div>
                <div class="header-meta-item">🆔 ${id?.slice(-8)}</div>
            </div>
        </div>

        <div class="summary">
            <div class="summary-card">
                <div class="summary-label">Status</div>
                <div class="summary-value ${execution.status === 'success' ? 'success' : 'error'}">${execution.status === 'success' ? '✓ Passed' : '✗ Failed'}</div>
            </div>
            <div class="summary-card">
                <div class="summary-label">Pass Rate</div>
                <div class="summary-value">${metrics.passRate}%</div>
            </div>
            <div class="summary-card">
                <div class="summary-label">Passed Steps</div>
                <div class="summary-value success">${metrics.passCount}</div>
            </div>
            <div class="summary-card">
                <div class="summary-label">Failed Steps</div>
                <div class="summary-value error">${metrics.failCount}</div>
            </div>
        </div>

        <div class="content">
            <div class="section">
                <h2 class="section-title">Test Steps</h2>
                ${steps.map((step, index) => `
                    <div class="step ${step.success ? 'passed' : 'failed'}">
                        <div class="step-header">
                            <div class="step-title">Step ${index + 1}: ${step.title || step.action}</div>
                            <div class="step-status ${step.success ? 'passed' : 'failed'}">${step.success ? '✓ Passed' : '✗ Failed'}</div>
                        </div>
                        <div class="step-details">
                            ${step.target ? `<div class="step-detail"><strong>Target Element</strong><code>${step.target}</code></div>` : ''}
                            ${step.data ? `<div class="step-detail"><strong>Input Data</strong><code>${JSON.stringify(step.data)}</code></div>` : ''}
                            ${step.reasoning ? `<div class="step-detail reasoning"><strong>🤖 AI Reasoning</strong><p>${step.reasoning}</p></div>` : ''}
                            ${!step.success && step.error ? `<div class="step-detail error"><strong>⚠️ Error</strong><p>${step.error}</p></div>` : ''}
                            ${step.screenshot ? `<img src="${step.screenshot}" alt="Step ${index + 1} screenshot" class="screenshot">` : ''}
                        </div>
                    </div>
                `).join('')}
            </div>

            ${execution.status === 'failed' && execution.result?.summary ? `
                <div class="section">
                    <h2 class="section-title">AI Analysis</h2>
                    <div class="ai-analysis">
                        <h3>🔍 Root Cause Analysis</h3>
                        <p>${execution.result.summary}</p>
                    </div>
                </div>
            ` : ''}
        </div>

        <div class="footer">
            Generated on ${new Date().toLocaleString()} | Test Execution ID: ${id}
        </div>
    </div>
</body>
</html>`;

            // Create and download the HTML file
            const blob = new Blob([html], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const filename = `${testName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-report-${id?.slice(-4)}.html`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export report:', error);
            const errorMessage = error.response?.data?.error || 'Failed to export report. Please try again.';
            setDialogConfig({
                title: 'Export Failed',
                message: errorMessage,
                type: 'alert',
                variant: 'danger'
            });
            setShowDialog(true);
        }
    };

    const handleExportAppium = async () => {
        try {
            setIsExporting(true);
            if (posthog) {
                posthog.capture('export_appium_clicked', {
                    executionId: execution?.id
                });
            }

            const response = await api.get(`/tests/executions/${id}/export/appium`, {
                // Fetch as text specifically for the modal
                responseType: 'text'
            });

            setExportScript(response.data);
            setShowExportModal(true);
            setShowActionsDropdown(false);
        } catch (error) {
            console.error('Failed to export Appium script:', error);
            const errorMessage = error.response?.data?.error || 'Failed to generate Appium script. Please try again.';
            setDialogConfig({
                title: 'Export Failed',
                message: errorMessage,
                type: 'alert',
                variant: 'danger'
            });
            setShowDialog(true);
        } finally {
            setIsExporting(false);
        }
    };

    const handleCopyScript = () => {
        navigator.clipboard.writeText(exportScript);
        showToast('Script copied to clipboard!');
    };

    const handleDownloadScript = () => {
        const blob = new Blob([exportScript], { type: 'text/javascript' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `rova_appium_export_${id.substring(0, 8)}.js`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showToast('Download started!');
    };

    const fetchPlans = async () => {
        try {
            const response = await executionPlanApi.getExecutionPlans();
            const plansData = response.data.rows || response.data.executionPlans || response.data;
            setPlans(Array.isArray(plansData) ? plansData : []);
        } catch (error) {
            console.error('Error fetching plans:', error);
        }
    };

    const handleAddToPlan = async () => {
        if (!selectedPlanId || !execution?.testGoalId) return;
        setIsAddingToPlan(true);
        try {
            await executionPlanApi.addGoalToPlan(selectedPlanId, execution.testGoalId);
            setShowPlanModal(false);
            setDialogConfig({
                title: 'Success',
                message: 'Goal added to execution plan successfully!',
                type: 'alert',
                variant: 'success'
            });
            setShowDialog(true);
        } catch (error) {
            setShowPlanModal(false);
            console.error('Error adding goal to plan:', error);
            setDialogConfig({
                title: 'Error',
                message: error.response?.data?.error || 'Failed to add goal to execution plan.',
                type: 'alert',
                variant: 'danger'
            });
            setShowDialog(true);
        } finally {
            setIsAddingToPlan(false);
            setShowActionsDropdown(false);
        }
    };

    const handleZoomImage = (imageUrl) => {
        if (!imageUrl) return;
        setZoomedImage(imageUrl);
    };

    const handleCloseZoom = () => {
        setZoomedImage(null);
    };

    const handleCreateBugReport = () => {
        // TODO: Implement Linear integration
        console.log('Create bug report:', id);
    };

    const handleDownloadAllScreenshots = async () => {
        try {
            const screenshotsToDownload = steps.filter(s => s.screenshot);

            if (screenshotsToDownload.length === 0) {
                setDialogConfig({
                    title: 'No Screenshots',
                    message: 'No screenshots available to download.',
                    type: 'alert',
                    variant: 'info'
                });
                setShowDialog(true);
                return;
            }

            // Dynamically import JSZip and FileSaver
            const JSZip = (await import('jszip')).default;
            const { saveAs } = await import('file-saver');

            const zip = new JSZip();
            const folder = zip.folder('screenshots');

            // Add each screenshot to the zip
            for (let i = 0; i < screenshotsToDownload.length; i++) {
                const step = screenshotsToDownload[i];
                const filename = `step-${String(i + 1).padStart(2, '0')}-${(step.title || step.action || 'screenshot').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`;

                // Fetch the image as blob
                const response = await fetch(step.screenshot);
                const blob = await response.blob();
                folder.file(filename, blob);
            }

            // Generate zip file
            const content = await zip.generateAsync({ type: 'blob' });

            // Download the zip
            const testName = execution.goalText?.substring(0, 30).replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'test';
            saveAs(content, `${testName}-screenshots-${id?.slice(-4)}.zip`);
        } catch (error) {
            console.error('Failed to download screenshots:', error);
            const errorMessage = error.response?.data?.error || 'Failed to download screenshots. Please try again.';
            setDialogConfig({
                title: 'Download Failed',
                message: errorMessage,
                type: 'alert',
                variant: 'danger'
            });
            setShowDialog(true);
        }
    };

    // Set header content
    useEffect(() => {
        if (!execution) return;

        const statusBadge = getStatusBadge(execution.status);
        const metrics = calculateMetrics();

        const headerContent = (
            <>
                <div className="header-breadcrumb">
                    <Link to="/" className="breadcrumb-item">Dashboard</Link>
                    <span className="breadcrumb-separator">/</span>
                    <Link to="/executions" className="breadcrumb-item">Test Runs</Link>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">
                        {execution.goalText?.substring(0, 30) || `Run #${id?.slice(-4)}`}
                    </span>
                </div>
                <div className="header-status">
                    <span className={`badge ${statusBadge.className}`}>
                        {statusBadge.label}
                    </span>
                    <span className="stat-item">
                        <CheckCircle size={14} /> {metrics.passCount}
                    </span>
                    <span className="stat-item error">
                        <XCircle size={14} /> {metrics.failCount}
                    </span>
                </div>

            </>
        );

        setHeaderLeft(headerContent);

        return () => setHeaderLeft(null);
    }, [execution, id, setHeaderLeft]);

    if (loading) {
        return (
            <div className="test-results loading-state">
                <div className="spinner"></div>
                <p>Loading test results...</p>
            </div>
        );
    }

    if (!execution) {
        return (
            <div className="test-results error-state">
                <AlertCircle size={48} />
                <h3>Test Execution Not Found</h3>
                <Link to="/" className="btn btn-primary">Back to Dashboard</Link>
            </div>
        );
    }

    const steps = execution.executedSteps
        ? (typeof execution.executedSteps === 'string'
            ? JSON.parse(execution.executedSteps)
            : execution.executedSteps)
        : [];

    const selectedStep = selectedStepIndex !== null ? steps[selectedStepIndex] : null;
    const metrics = calculateMetrics();

    return (
        <div className="test-results">
            {/* Test Header Info */}
            <div className="test-header-info">
                <div className="test-title-section">
                    <h1>Test Run #{id?.slice(-4)}: {execution.goalText}</h1>
                    <div className="test-meta">
                        <span className="meta-item">
                            <Smartphone size={14} />
                            {execution.metadata?.deviceName
                                ? `${execution.metadata.deviceName}${execution.metadata.deviceOs ? ` (${execution.metadata.deviceOs})` : ''}`
                                : 'Device information not available'
                            }
                        </span>
                        <span className="meta-item">
                            <Clock size={14} />
                            {formatDuration(execution.duration)}
                        </span>
                        <span className="meta-item">
                            <Calendar size={14} />
                            {new Date(execution.createdAt).toLocaleString()}
                        </span>
                    </div>
                </div>
                <div className="test-actions">
                    <button className="btn btn-secondary btn-sm btn-ghost" onClick={handleExportReport}>
                        <Download size={16} />
                        Export Report
                    </button>

                    <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => handleRerun(execution, e)}
                        disabled={rerunLoading}
                    >
                        {rerunLoading ? (
                            <span className="spinner-small" style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }}></span>
                        ) : (
                            <RefreshCw size={16} />
                        )}
                        {rerunLoading ? 'Rerunning...' : 'Rerun Test'}
                    </button>
                    <div className="actions-wrapper">
                        <button
                            className="btn btn-secondary btn-sm btn-ghost"
                            onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                            <span>More</span>
                            <ChevronDown size={14} style={{ opacity: 0.7 }} />
                        </button>
                        {showActionsDropdown && (
                            <div className="actions-dropdown">
                                <button
                                    className="dropdown-item"
                                    onClick={() => {
                                        fetchPlans();
                                        setShowPlanModal(true);
                                        setShowActionsDropdown(false);
                                    }}
                                >
                                    Add goal to plan
                                </button>
                                <button
                                    className="dropdown-item"
                                    onClick={handleExportAppium}
                                    disabled={isExporting}
                                >
                                    {isExporting ? 'Generating Appium script...' : 'Export to Appium'}
                                </button>
                                <Link className='dropdown-item'
                                    to={`/goals/${execution.testGoalId}`}
                                >
                                    View goal
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="results-content">
                {/* Left Panel - Test Steps */}
                <div className="steps-panel">
                    <div className="panel-header">
                        <h3>TEST STEPS ({steps.length})</h3>
                        {/* <button className="btn btn-ghost btn-sm">
                            <Filter size={14} />
                        </button> */}
                    </div>

                    <div className="steps-list">
                        {steps.map((step, index) => (
                            <div
                                key={index}
                                className={`step-item ${!step.success ? 'failed' : 'passed'} ${selectedStepIndex === index ? 'selected' : ''}`}
                                onClick={() => setSelectedStepIndex(index)}
                            >
                                <div className="step-header">
                                    {getStatusIcon(step.success)}
                                    <span className="step-name">{step.title || step.action}</span>
                                    <span className="step-duration">{formatDuration(step.duration)}</span>
                                </div>

                                {/* Show details when selected */}
                                {selectedStepIndex === index && (
                                    <div className="step-details">
                                        {step.target && (
                                            <div className="step-detail-item">
                                                <span className="detail-label">Target:</span>
                                                <code className="result__detail-value">{step.target}</code>
                                            </div>
                                        )}

                                        {step.data && (
                                            <div className="step-detail-item">
                                                <span className="detail-label">Data:</span>
                                                <code className="result__detail-value">{JSON.stringify(step.data)}</code>
                                            </div>
                                        )}

                                        {step.reasoning && (
                                            <div className="step-detail-item reasoning">
                                                <span className="detail-label">
                                                    <Sparkles size={12} />
                                                    AI Reasoning:
                                                </span>
                                                <p className="result__detail-value">{step.reasoning}</p>
                                            </div>
                                        )}

                                        {step.confidence && (
                                            <div className="step-detail-item">
                                                <span className="detail-label">Confidence:</span>
                                                <div className="confidence-bar-mini">
                                                    <div
                                                        className="confidence-fill"
                                                        style={{ width: `${step.confidence * 100}%` }}
                                                    />
                                                    <span className="confidence-text">{(step.confidence * 100).toFixed(0)}%</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!step.success && step.error && (
                                    <div className="step-error">
                                        <AlertCircle size={12} />
                                        Error: {step.error}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Center Panel - Screenshot & Logs */}
                <div className="screenshot-panel">
                    {/* <div className="screenshot-header">
                        <div className="screenshot-actions">
                            <button className="btn btn-ghost btn-sm">
                                <Link to={selectedStep?.screenshot} download target='_blank'>
                                    <Download size={14} />
                                </Link>
                            </button>
                            <button className="btn btn-ghost btn-sm">
                                <Copy size={14} />
                            </button>
                        </div>
                        {selectedStep?.screenshot && (
                            <span className="screenshot-resolution">
                                Resolution: 1179 × 2556
                            </span>
                        )}
                    </div> */}

                    <div className="screenshot-viewer">
                        {execution.status === 'error' ? (
                            <div className="crash-view">
                                <div className="crash-header">
                                    <div className="crash-icon-wrapper">
                                        <AlertCircle size={32} />
                                    </div>
                                    <div className="crash-title-group">
                                        <h2>Execution Core Failure</h2>
                                        <span className="crash-timestamp">System Error Logs • {new Date(execution.updatedAt || execution.createdAt).toLocaleTimeString()}</span>
                                    </div>
                                </div>
                                <div className="crash-message-card">
                                    <div className="card-indicator"></div>
                                    <pre className="crash-text">{execution.errorMessage || 'An unknown execution error occurred during initialization.'}</pre>
                                </div>
                                <div className="crash-footer">
                                    <p>This failure typically happens during device provisioning or cloud environment setup. The session was gracefully terminated to prevent resource leaks.</p>
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={(e) => handleRerun(execution, e)}
                                        disabled={rerunLoading}
                                    >
                                        <RefreshCw size={14} />
                                        Rerun Job
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="device-frame">
                                <div className="device-notch"></div>
                                <div className="device-screen">
                                    {selectedStep?.screenshot ? (
                                        <img
                                            src={selectedStep.screenshot}
                                            alt={`Step ${selectedStepIndex + 1}`}
                                            className="screen-image screen-image--zoomable"
                                            onClick={() => handleZoomImage(selectedStep.screenshot)}
                                            title='Click for Fullscreen'
                                        />
                                    ) : (
                                        <div className="screen-placeholder">
                                            <div className="placeholder-content">
                                                <Smartphone size={48} />
                                                <p>No screenshot available</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="logs-section">
                        <div className="logs-tabs">
                            <button
                                className={`tab ${activeLogTab === 'video' ? 'active' : ''}`}
                                onClick={() => setActiveLogTab('video')}
                            >
                                Video Recording
                            </button>
                            <button
                                className={`tab ${activeLogTab === 'device' ? 'active' : ''}`}
                                onClick={() => setActiveLogTab('device')}
                            >
                                Device Logs
                            </button>
                            {/* <button
                                className={`tab ${activeLogTab === 'network' ? 'active' : ''}`}
                                onClick={() => setActiveLogTab('network')}
                            >
                                Network
                            </button> */}
                        </div>

                        <div className="logs-content">
                            {activeLogTab === 'video' && (
                                <div className="video-content">
                                    {videoLoading ? (
                                        <div className="video-loading">Loading video...</div>
                                    ) : videoUrl ? (
                                        <video
                                            className="session-video"
                                            controls
                                            autoPlay={false}
                                            muted
                                            src={videoUrl}
                                            ref={(el) => {
                                                if (el) {
                                                    el.defaultPlaybackRate = 2.0;
                                                    el.playbackRate = 2.0;
                                                }
                                            }}
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                    ) : execution?.metadata?.sessionArn ? (
                                        <div className="video-empty">Video not available for this session</div>
                                    ) : (
                                        <div className="video-empty">Video recording not available for this execution</div>
                                    )}
                                </div>
                            )}

                            {activeLogTab === 'device' && (
                                <div className="logcat-content">
                                    {logcatLoading ? (
                                        <div className="logcat-loading">Loading device logs...</div>
                                    ) : logcatContent ? (
                                        <pre className="logcat-text">{logcatContent}</pre>
                                    ) : execution?.metadata?.sessionArn ? (
                                        <div className="logcat-empty">
                                            Click to load {execution.platform === 'ios' ? 'iOS device logs' : 'Android logcat'}
                                        </div>
                                    ) : (
                                        <div className="logcat-empty">Device logs not available for this execution</div>
                                    )}
                                </div>
                            )}

                            {activeLogTab === 'network' && (
                                <div className="logcat-empty">Network logs coming soon</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Panel - AI Analysis */}
                <div className="analysis-panel">
                    <div className="panel-header">
                        <Sparkles size={16} className="ai-icon" />
                        <h3>AI Analysis</h3>
                    </div>

                    {execution.status != 'success' && execution.result && (
                        <>
                            {/* Root Cause Analysis */}
                            <div className="analysis-section">
                                <h4>Root Cause Analysis</h4>
                                <div className="analysis-content">
                                    <p>
                                        {execution.result.summary || execution.errorMessage}
                                    </p>
                                    {execution.result.reasoning && (
                                        <p className="reasoning">
                                            {execution.result.reasoning}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Suggested Fix */}
                            {/* <div className="analysis-section suggested-fix">
                                <div className="section-header">
                                    <h4>
                                        <Zap size={14} />
                                        Suggested Fix
                                    </h4>
                                    <button className="btn btn-sm btn-primary">
                                        Apply Fix
                                    </button>
                                </div>
                                <div className="code-block">
                                    
                                </div>
                                <p className="fix-description">
                                    Added a conditional check to close the promo modal before attempting checkout.
                                </p>
                            </div> */}
                        </>
                    )}

                    {execution.status === 'success' && (
                        <div className="analysis-section success-summary">
                            <CheckCircle size={24} className="success-icon" />
                            <h4>Test Passed Successfully</h4>
                            <p>All {metrics.totalSteps} steps completed without errors.</p>
                            <div className="metrics-grid">
                                <div className="metric">
                                    <span className="metric-label">Pass Rate</span>
                                    <span className="metric-value">{metrics.passRate}%</span>
                                </div>
                                <div className="metric">
                                    <span className="metric-label">Duration</span>
                                    <span className="metric-value">{formatDuration(execution.duration)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Similar Failures */}
                    {/* {similarFailures.length > 0 && execution.status === 'failed' && (
                        <div className="analysis-section similar-failures">
                            <h4>Similar Failures (Last 24h)</h4>
                            <div className="failures-list">
                                {similarFailures.map((failure) => (
                                    <div key={failure.id} className="failure-item">
                                        <div className="failure-icon">
                                            <AlertCircle size={16} />
                                        </div>
                                        <div className="failure-info">
                                            <span className="failure-name">{failure.name} #{failure.id}</span>
                                            <span className="failure-meta">
                                                {failure.environment} • {failure.timeAgo}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button className="btn btn-ghost btn-sm btn-full">
                                <BarChart3 size={14} />
                                Chat with AI Assistant
                            </button>
                        </div>
                    )} */}
                </div>
            </div>

            {/* Screenshot Gallery */}
            {
                steps.length > 0 && steps.some(s => s.screenshot) && (
                    <div className="screenshot-gallery-section">
                        <div className="section-header">
                            <h3>Screenshot Gallery ({steps.filter(s => s.screenshot).length})</h3>
                            <button className="btn btn-primary btn-sm" onClick={handleDownloadAllScreenshots}>
                                <Download size={14} />
                                Download All
                            </button>
                        </div>
                        <div className="screenshot-carousel">
                            {steps.map((step, index) =>
                                step.screenshot && (
                                    <div
                                        key={index}
                                        className={`carousel-item ${selectedStepIndex === index ? 'active' : ''}`}
                                        onClick={() => setSelectedStepIndex(index)}
                                    >
                                        <div className="carousel-image-container" onClick={() => handleZoomImage(step.screenshot)}>
                                            <img src={step.screenshot} alt={`Step ${index + 1}`} />
                                            <div className="carousel-zoom-hint">
                                                <Sparkles size={16} />
                                            </div>
                                        </div>
                                        <div className="carousel-caption">
                                            <span className="step-number">Step {index + 1}</span>
                                            <span className="step-action">{step.title || step.action}</span>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                )
            }

            <Dialog
                isOpen={showDialog}
                onClose={() => setShowDialog(false)}
                title={dialogConfig.title}
                message={dialogConfig.message}
                type={dialogConfig.type}
                variant={dialogConfig.variant}
                confirmText="OK"
            />
            {/* Zoom Overlay */}
            {zoomedImage && (
                <div className="zoom-overlay" onClick={handleCloseZoom}>
                    <button className="zoom-close" onClick={handleCloseZoom}>
                        <X size={24} />
                    </button>
                    <div className="zoom-container" onClick={(e) => e.stopPropagation()}>
                        <img src={zoomedImage} alt="Zoomed screenshot" className="zoom-image" />
                    </div>
                </div>
            )}
            {/* Add to Plan Modal */}
            {showPlanModal && (
                <div className="dialog-overlay" onClick={() => setShowPlanModal(false)}>
                    <div className="dialog-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                        <div className="dialog-header">
                            <h3 className="dialog-title">Add Goal to Execution Plan</h3>
                            <button className="dialog-close" onClick={() => setShowPlanModal(false)}>
                                ×
                            </button>
                        </div>

                        <div className="dialog-body">
                            <p style={{ marginBottom: 'var(--spacing-md)', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                Select an execution plan to add this goal to.
                            </p>

                            <div>
                                <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                                    Execution Plan
                                </label>
                                <select
                                    value={selectedPlanId}
                                    onChange={(e) => setSelectedPlanId(e.target.value)}
                                    className="plan-select"
                                >
                                    <option value="">Select a plan...</option>
                                    {plans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.name} ({plan.platform === 'ios' ? 'iOS' : 'Android'})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="dialog-footer">
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setShowPlanModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleAddToPlan}
                                disabled={!selectedPlanId || isAddingToPlan}
                            >
                                {isAddingToPlan ? 'Adding...' : 'Add to Plan'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Appium Export Modal */}
            {showExportModal && (
                <div className="dialog-overlay" onClick={() => setShowExportModal(false)}>
                    <div className="dialog-container appium-export-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h3 className="dialog-title">Appium Script Export</h3>
                            <button className="dialog-close" onClick={() => setShowExportModal(false)}>
                                ×
                            </button>
                        </div>

                        <div className="dialog-body">
                            <p style={{ marginBottom: 'var(--spacing-md)', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                Edit and download your ready-to-run WebdriverIO script.
                            </p>

                            <div className="script-editor-wrapper">
                                <textarea
                                    className="script-editor"
                                    value={exportScript}
                                    onChange={(e) => setExportScript(e.target.value)}
                                    spellCheck={false}
                                />
                            </div>
                        </div>

                        <div className="dialog-footer">
                            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => setShowExportModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleCopyScript}
                                >
                                    <Copy size={16} />
                                    Copy Script
                                </button>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleDownloadScript}
                                >
                                    <Download size={16} />
                                    Download (.js)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TestResults;
